import React, { useEffect, useState } from "react";
import { RxCross2 } from "react-icons/rx";
import { IoChevronDownSharp } from "react-icons/io5";
import { MdCampaign, MdPeopleAlt } from "react-icons/md";
import { useDispatch } from "react-redux";
import { setAudienceInterestFilter, setCreatorInterestFilter } from "../../redux/filters/actions";
import Multiselect from "multiselect-react-dropdown";
import InfoIconTooltip from "./InfoIconTooltip";
import axiosInstance from "../../interseptor";
import { useLocation, useSearchParams } from "react-router-dom";
import useDropdownPosition from "./useDropdownPosition";

const percentageArr = [
  "1", "5", "10", "15", "20", "25", "30", "35", "40", "45",
  "50", "55", "60", "65", "70", "75", "80", "85", "90", "95"
];

const CreatorInterest = ({ selectedPlatform }) => {
  const dispatch = useDispatch();
  const [searchParam, setSearchParam] = useSearchParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // Get URL parameters
  const creatorInterestUrl = searchParams.get("creatorInterest");
  const audienceInterestUrl = searchParams.get("audienceInterest");

  // State management
  const [creatorInterestData, setCreatorInterestData] = useState([]);
  const [audienceInterestData, setAudienceInterestData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchString, setSearchString] = useState("");
  const [selectedOptions, setSelectedOptions] = useState({});

  const { dropdownRef, buttonRef, setIsDropdownOpen, isDropdownOpen } = useDropdownPosition();

  // Parse interest strings from URL - updated to handle commas properly
  const parseInterestString = (interestStr) => {
    if (!interestStr) return [];
    return interestStr.split(/,(?!\s)/).map((value) => {
      const parts = value.split("-k-");
      return {
        id: parts[0],
        name: parts[1] || parts[0],
        percentage: parts[2] || "5"
      };
    });
  };

  const audienceInterestArray = parseInterestString(audienceInterestUrl);
  const creatorInterestArray = parseInterestString(creatorInterestUrl);

  const fetchInterestData = async () => {
    setCreatorInterestData([]);
    setAudienceInterestData([]);
    
    try {
      setLoading(true);
      setError(null);

      const formData = new FormData();
      formData.append("filter", "interests");
      formData.append("logintype", "getfilters");
      formData.append("device_id", "search");
      formData.append("platform", selectedPlatform);
      formData.append("search_string", searchString);

      const response = await axiosInstance.post("livesearch/getfilters", formData);
      const result = response.data;

      if (result.status === 1) {
      // console.log("Raw API Data:", result.response.data.interests); 
        const formattedData = result.response.data.interests.map(item => ({
          id: item.id || item.name, 
          name: item.name
        }));
        
        setCreatorInterestData(formattedData);
        setAudienceInterestData(formattedData);
      } else {
        setError(result.msg);
        console.error("Error:", result.msg);
      }
    } catch (error) {
      setError("An error occurred while fetching data.");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle creator interest selection - updated to handle commas
  const handleCreatorInterest = (selectedList) => {
    const selectedValues = selectedList.map(item => 
      `${item.id}-k-${item.name.replace(/,/g, '')}`
    );
    
    dispatch(setCreatorInterestFilter(selectedValues));
    setSearchParam((searchParams) => {
      selectedValues.length > 0
        ? searchParams.set("creatorInterest", selectedValues.join(","))
        : searchParams.delete("creatorInterest");
      return searchParams;
    });
  };

  // Handle audience interest selection - updated to handle commas
  const handleAudienceInterest = (selectedList) => {
    const updatedValues = selectedList.map(
      item => `${item.id}-k-${item.name.replace(/,/g, "")}-k-${selectedOptions[item.id] || "5"}`
    );
    
    dispatch(setAudienceInterestFilter(updatedValues));
    setSearchParam((searchParams) => {
      updatedValues.length > 0
        ? searchParams.set("audienceInterest", updatedValues.join(","))
        : searchParams.delete("audienceInterest");
      return searchParams;
    });
    
    setIsDropdownOpen(false);
  };

  // Handle percentage change for audience interests - updated to handle commas
  const handlePercentageChange = (id, value) => {
    setSelectedOptions(prev => {
      const updatedOptions = {
        ...prev,
        [id]: value
      };
      
      const updatedValues = audienceInterestArray.map(
        item => `${item.id}-k-${item.name.replace(/,/g, "")}-k-${updatedOptions[item.id] || item.percentage || "5"}`
      );

      dispatch(setAudienceInterestFilter(updatedValues));
      setSearchParam((searchParams) => {
        updatedValues.length > 0
          ? searchParams.set("audienceInterest", updatedValues.join(","))
          : searchParams.delete("audienceInterest");
        return searchParams;
      });

      return updatedOptions;
    });

    setIsDropdownOpen(false);
  };

  // Handle search initialization
  const handleSearchInterest = () => {
    setSearchString("");
    setIsDropdownOpen(true);
    fetchInterestData();
  };

  // Effects
  useEffect(() => {
    if (searchString === "") {
      fetchInterestData();
    }
  }, [searchString]);

  useEffect(() => {
    if (searchString.length > 1) {
      fetchInterestData();
    }
  }, [searchString]);

  // Prepare selected values for both interest types
  const selectedValueAudience = audienceInterestArray.map(value => ({
    id: value.id,
    name: value.name
  }));

  const selectedValueCreator = creatorInterestArray.map(value => ({
    id: value.id,
    name: value.name
  }));

  // Common styles for Multiselect component
  const multiselectStyles = {
    multiselectContainer: {
      width: "100%",
      borderRadius: "0.375rem",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    },
    searchBox: {
      border: "0.6px solid #D9D9D9",
      borderRadius: "0.375rem",
    },
    inputField: {
      fontSize: "14px",
      fontWeight: "400",
      color: "#333",
    },
    chips: {
      background: "#6d5eac",
      color: "#fff",
    },
    option: {
      color: "#333",
    },
    groupHeading: {
      color: "#6d5eac",
      background: "#6d5eac",
    },
    dropdownList: {
      background: "#6d5eac",
      "&:hover": {
        background: "#6d5eac",
      },
    },
  };

  return (
    <div ref={buttonRef} className="CreatorInterest flex justify-start">
      <div className="relative w-full">
        <button
          onClick={handleSearchInterest}
          className={`filterTextColor flex items-center bg-white ${
            isDropdownOpen ? "font-semibold" : "font-normal"
          }`}
        >
          Interests
          <span className={`${isDropdownOpen ? "" : ""} flex items-center`}>
            {isDropdownOpen ? (
              <RxCross2
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDropdownOpen(false);
                }}
                className="ml-2 text-lg"
              />
            ) : (
              <IoChevronDownSharp className="ml-2 text-lg" />
            )}
          </span>
        </button>
        
        <ul
          ref={dropdownRef}
          className={`absolute w-fit z-50 p-5 bg-white rounded-lg shadow-lg ${
            isDropdownOpen ? "block" : "hidden"
          }`}
        >
          <div className="flex gap-5">
            {/* Audience Interest Section */}
            <div onClick={handleSearchInterest} className="flex flex-col gap-2 w-full">
              <div className="flex items-center gap-2">
                <MdPeopleAlt className="text-2xl" /> Audience{" "}
                <InfoIconTooltip
                  infoContent={
                    "Identify influencers by their audience interests. We determine interest of audience by analyzing engagement data. You can add several interests and your search results will be refined with influencers that have ANY of the specified interests in their audience."
                  }
                />
              </div>
              
              <Multiselect
                className="bg-white"
                options={audienceInterestData}
                displayValue="name"
                selectedValues={selectedValueAudience}
                onSelect={handleAudienceInterest}
                onRemove={handleAudienceInterest}
                placeholder="Search Interest"
                loading={loading}
                onSearch={(searchInput) => setSearchString(searchInput)}
                style={multiselectStyles}
              />

              {audienceInterestArray.map((item) => (
                <div key={item.id} className="flex items-center justify-between">
                  <span>{item.name}</span>
{/* {                  console.log(item.percentage)} */}
                  <select
                    value={selectedOptions[item.id] || item.percentage || "5"}
                    onChange={(e) => handlePercentageChange(item.id, e.target.value)}
                    className="cursor-pointer hover:bg-gray-200 rounded-lg shadow-md"
                    style={{
                      border: "0.6px solid #D9D9D9",
                      maxHeight: "400px",
                      overflowY: "auto",
                    }}
                  >
                    {percentageArr.map((option) => (
                      <option key={option} value={option}>
                        {`>${option}%`}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            {/* Creator Interest Section */}
            <div onClick={handleSearchInterest} className="flex flex-col gap-2 w-full">
              <div className="flex items-center gap-2">
                <MdCampaign className="text-2xl" /> Influencer{" "}
                <InfoIconTooltip
                  infoContent={
                    "Identify influencers by their interests. We determine interest of influencers by analyzing posts for caption text, mentions, hashtags and location tags. You can add several interests and your search results will be refined with influencers that have ANY of the specified interests."
                  }
                />
              </div>
              
              <Multiselect
                className="bg-white"
                options={creatorInterestData}
                displayValue="name"
                onSelect={handleCreatorInterest}
                onRemove={handleCreatorInterest}
                selectedValues={selectedValueCreator}
                placeholder="Search Interest"
                loading={loading}
                onSearch={(searchInput) => setSearchString(searchInput)}
                style={multiselectStyles}
              />
            </div>
          </div>
        </ul>
      </div>
    </div>
  );
};

export default CreatorInterest;


// import React, { useEffect, useState } from "react";
// import { RxCross2 } from "react-icons/rx";
// import { IoChevronDownSharp } from "react-icons/io5";
// import { MdCampaign, MdPeopleAlt } from "react-icons/md";
// import { useDispatch } from "react-redux";
// import { setAudienceInterestFilter, setCreatorInterestFilter } from "../../redux/filters/actions";
// import Multiselect from "multiselect-react-dropdown";
// import InfoIconTooltip from "./InfoIconTooltip";
// import axiosInstance from "../../interseptor";
// import { useLocation, useSearchParams } from "react-router-dom";
// import useDropdownPosition from "./useDropdownPosition";

// const percentageArr = [
//   "1", "5", "10", "15", "20", "25", "30", "35", "40", "45",
//   "50", "55", "60", "65", "70", "75", "80", "85", "90", "95"
// ];

// const CreatorInterest = ({ selectedPlatform }) => {
//   const dispatch = useDispatch();
//   const [searchParam, setSearchParam] = useSearchParams();
//   const location = useLocation();
//   const searchParams = new URLSearchParams(location.search);

//   // Get URL parameters
//   const creatorInterestUrl = searchParams.get("creatorInterest");
//   const audienceInterestUrl = searchParams.get("audienceInterest");

//   // State management
//   const [creatorInterestData, setCreatorInterestData] = useState([]);
//   const [audienceInterestData, setAudienceInterestData] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [searchString, setSearchString] = useState("");
//   const [selectedOptions, setSelectedOptions] = useState({});

//   const { dropdownRef, buttonRef, setIsDropdownOpen, isDropdownOpen } = useDropdownPosition();

//   // Parse interest strings from URL - similar to location parsing
//   const parseInterestString = (interestStr) => {
//     if (!interestStr) return [];
//     return interestStr.split(/,(?!\s)/).map((value) => {
//       const parts = value.split("-k-");
//       return {
//         id: parts[0],
//         name: parts[1] || parts[0],
//         percentage: parts[2] || "5"
//       };
//     });
//   };

//   const audienceInterestArray = parseInterestString(audienceInterestUrl);
//   const creatorInterestArray = parseInterestString(creatorInterestUrl);

//   // Fetch interest data from API
//   const fetchInterestData = async () => {
//     setCreatorInterestData([]);
//     setAudienceInterestData([]);
    
//     try {
//       setLoading(true);
//       setError(null);

//       const formData = new FormData();
//       formData.append("filter", "interests");
//       formData.append("logintype", "getfilters");
//       formData.append("device_id", "search");
//       formData.append("platform", selectedPlatform);
//       formData.append("search_string", searchString);

//       const response = await axiosInstance.post("livesearch/getfilters", formData);
//       const result = response.data;

//       if (result.status === 1) {
//         // Make sure data has both id and name properties
//         const formattedData = result.response.data.interests.map(item => ({
//           id: item.id || item.name, // Ensure we have an id
//           name: item.name
//         }));
        
//         setCreatorInterestData(formattedData);
//         setAudienceInterestData(formattedData);
//       } else {
//         setError(result.msg);
//         console.error("Error:", result.msg);
//       }
//     } catch (error) {
//       setError("An error occurred while fetching data.");
//       console.error("Error:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Handle creator interest selection - using both id and name
//   const handleCreatorInterest = (selectedList) => {
//     const selectedValues = selectedList.map(item => 
//       `${item.id}-k-${item.name}`
//     );
    
//     dispatch(setCreatorInterestFilter(selectedValues));
//     setSearchParam((searchParams) => {
//       selectedValues.length > 0
//         ? searchParams.set("creatorInterest", selectedValues.join(","))
//         : searchParams.delete("creatorInterest");
//       return searchParams;
//     });
//   };

//   // Handle audience interest selection - using id, name, and percentage
//   const handleAudienceInterest = (selectedList) => {
//     const updatedValues = selectedList.map(
//       item => `${item.id}-k-${item.name}-k-${selectedOptions[item.id] || "5"}`
//     );
    
//     dispatch(setAudienceInterestFilter(updatedValues));
//     setSearchParam((searchParams) => {
//       updatedValues.length > 0
//         ? searchParams.set("audienceInterest", updatedValues.join(","))
//         : searchParams.delete("audienceInterest");
//       return searchParams;
//     });
    
//     setIsDropdownOpen(false);
//   };

//   // Handle percentage change for audience interests
//   const handlePercentageChange = (id, value) => {
//     setSelectedOptions(prev => {
//       const updatedOptions = {
//         ...prev,
//         [id]: value
//       };
      
//       const updatedValues = audienceInterestArray.map(
//         item => `${item.id}-k-${item.name}-k-${updatedOptions[item.id] || item.percentage || "5"}`
//       );

//       dispatch(setAudienceInterestFilter(updatedValues));
//       setSearchParam((searchParams) => {
//         updatedValues.length > 0
//           ? searchParams.set("audienceInterest", updatedValues.join(","))
//           : searchParams.delete("audienceInterest");
//         return searchParams;
//       });

//       return updatedOptions;
//     });

//     setIsDropdownOpen(false);
//   };

//   // Handle search initialization
//   const handleSearchInterest = () => {
//     setSearchString("");
//     setIsDropdownOpen(true);
//     fetchInterestData();
//   };

//   // Effects
//   useEffect(() => {
//     if (searchString === "") {
//       fetchInterestData();
//     }
//   }, [searchString]);

//   useEffect(() => {
//     if (searchString.length > 1) {
//       fetchInterestData();
//     }
//   }, [searchString]);

//   // Prepare selected values for both interest types
//   const selectedValueAudience = audienceInterestArray.map(value => ({
//     id: value.id,
//     name: value.name
//   }));

//   const selectedValueCreator = creatorInterestArray.map(value => ({
//     id: value.id,
//     name: value.name
//   }));

//   // Common styles for Multiselect component
//   const multiselectStyles = {
//     multiselectContainer: {
//       width: "100%",
//       borderRadius: "0.375rem",
//       boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
//     },
//     searchBox: {
//       border: "0.6px solid #D9D9D9",
//       borderRadius: "0.375rem",
//     },
//     inputField: {
//       fontSize: "14px",
//       fontWeight: "400",
//       color: "#333",
//     },
//     chips: {
//       background: "#6d5eac",
//       color: "#fff",
//     },
//     option: {
//       color: "#333",
//     },
//     groupHeading: {
//       color: "#6d5eac",
//       background: "#6d5eac",
//     },
//     dropdownList: {
//       background: "#6d5eac",
//       "&:hover": {
//         background: "#6d5eac",
//       },
//     },
//   };

//   return (
//     <div ref={buttonRef} className="CreatorInterest flex justify-start">
//       <div className="relative w-full">
//         <button
//           onClick={handleSearchInterest}
//           className={`filterTextColor flex items-center bg-white ${
//             isDropdownOpen ? "font-semibold" : "font-normal"
//           }`}
//         >
//           Interests
//           <span className={`${isDropdownOpen ? "" : ""} flex items-center`}>
//             {isDropdownOpen ? (
//               <RxCross2
//                 onClick={(e) => {
//                   e.stopPropagation();
//                   setIsDropdownOpen(false);
//                 }}
//                 className="ml-2 text-lg"
//               />
//             ) : (
//               <IoChevronDownSharp className="ml-2 text-lg" />
//             )}
//           </span>
//         </button>
        
//         <ul
//           ref={dropdownRef}
//           className={`absolute w-fit z-50 p-5 bg-white rounded-lg shadow-lg ${
//             isDropdownOpen ? "block" : "hidden"
//           }`}
//         >
//           <div className="flex gap-5">
//             {/* Audience Interest Section */}
//             <div onClick={handleSearchInterest} className="flex flex-col gap-2 w-full">
//               <div className="flex items-center gap-2">
//                 <MdPeopleAlt className="text-2xl" /> Audience{" "}
//                 <InfoIconTooltip
//                   infoContent={
//                     "Identify influencers by their audience interests. We determine interest of audience by analyzing engagement data. You can add several interests and your search results will be refined with influencers that have ANY of the specified interests in their audience."
//                   }
//                 />
//               </div>
              
//               <Multiselect
//                 className="bg-white"
//                 options={audienceInterestData}
//                 displayValue="name"
//                 selectedValues={selectedValueAudience}
//                 onSelect={handleAudienceInterest}
//                 onRemove={handleAudienceInterest}
//                 placeholder="Search Interest"
//                 loading={loading}
//                 onSearch={(searchInput) => setSearchString(searchInput)}
//                 style={multiselectStyles}
//               />

//               {audienceInterestArray.map((item) => (
//                 <div key={item.id} className="flex items-center justify-between">
//                   <span>{item.name}</span>
//                   <select
//                     value={selectedOptions[item.id] || item.percentage || "5"}
//                     onChange={(e) => handlePercentageChange(item.id, e.target.value)}
//                     className="cursor-pointer hover:bg-gray-200 rounded-lg shadow-md"
//                     style={{
//                       border: "0.6px solid #D9D9D9",
//                       maxHeight: "400px",
//                       overflowY: "auto",
//                     }}
//                   >
//                     {percentageArr.map((option) => (
//                       <option key={option} value={option}>
//                         {`>${option}%`}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//               ))}
//             </div>

//             {/* Creator Interest Section */}
//             <div onClick={handleSearchInterest} className="flex flex-col gap-2 w-full">
//               <div className="flex items-center gap-2">
//                 <MdCampaign className="text-2xl" /> Influencer{" "}
//                 <InfoIconTooltip
//                   infoContent={
//                     "Identify influencers by their interests. We determine interest of influencers by analyzing posts for caption text, mentions, hashtags and location tags. You can add several interests and your search results will be refined with influencers that have ANY of the specified interests."
//                   }
//                 />
//               </div>
              
//               <Multiselect
//                 className="bg-white"
//                 options={creatorInterestData}
//                 displayValue="name"
//                 onSelect={handleCreatorInterest}
//                 onRemove={handleCreatorInterest}
//                 selectedValues={selectedValueCreator}
//                 placeholder="Search Interest"
//                 loading={loading}
//                 onSearch={(searchInput) => setSearchString(searchInput)}
//                 style={multiselectStyles}
//               />
//             </div>
//           </div>
//         </ul>
//       </div>
//     </div>
//   );
// };

// export default CreatorInterest;

// import React, { useRef, useState, useEffect } from "react";
// import { RxCross2 } from "react-icons/rx";
// import { IoChevronDownSharp } from "react-icons/io5";
// import { MdCampaign, MdPeopleAlt } from "react-icons/md";
// import { useDispatch } from "react-redux";
// import { setAudienceInterestFilter, setCreatorInterestFilter } from "../../redux/filters/actions";
// import MultiSelectDropdown from "./MultiSelectDropdown";
// import InfoIconTooltip from "./InfoIconTooltip";
// import axiosInstance from "../../interseptor";
// import { useLocation, useSearchParams } from "react-router-dom";
// import useDropdownPosition from "./useDropdownPosition";

// const percentageArr = ["1", "5", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55", "60", "65", "70", "75", "80", "85", "90", "95"];

// const CreatorInterest = ({ selectedPlatform }) => {
//   const dispatch = useDispatch();
//   const [searchParam, setSearchParam] = useSearchParams();
//   const location = useLocation();
//   const searchParams = new URLSearchParams(location.search);

//   const creatorInterestUrl = searchParams.get("creatorInterest");
//   const audienceInterestUrl = searchParams.get("audienceInterest");

//   const creatorInterestArray = creatorInterestUrl ? creatorInterestUrl.split(",") : [];
//   const audienceInterestArray = audienceInterestUrl ? audienceInterestUrl.split(",") : [];

//   const {dropdownRef, buttonRef, setIsDropdownOpen, isDropdownOpen} = useDropdownPosition();

//   const [creatorInterest, setCreatorInterest] = useState([]);
//   const [audienceInterest, setAudienceInterest] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [selectedOptions, setSelectedOptions] = useState({});

//   const fetchCreatorInterestData = async () => {
//     setCreatorInterest([]);
//     setAudienceInterest([]);
//     try {
//       setLoading(true);
//       setError(null);

//       const formData = new FormData();
//       formData.append("filter", "interests");
//       formData.append("logintype", "getfilters");
//       formData.append("device_id", "search");
//       formData.append("platform", selectedPlatform);

//       const response = await axiosInstance.post("livesearch/getfilters", formData);
//       const result = response.data;
// console.log('dsdsd',response.data )
//       if (result.status === 1) {
//         setCreatorInterest(result.response.data.interests);
//         setAudienceInterest(result.response.data.interests);
//       } else {
//         setError(result.msg);
//         console.error("Error:", result.msg);
//       }
//     } catch (error) {
//       setError("An error occurred while fetching data.");
//       console.error("Error:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleSearchCreatorInterest = () => {
//     fetchCreatorInterestData();
//     setIsDropdownOpen(!isDropdownOpen);
//   };

//   const handleInterestChange = (selectedList) => {
//      const selectedValues = selectedList.map((item) => item.name);
//     //  const selectedValues = selectedList?.map(
//     //   (item) => item.id + "-k-" + item.name
//     // );
//     dispatch(setCreatorInterestFilter(selectedValues));

//     setSearchParam((searchParams) => {
//       if (selectedValues.length > 0) {
//         searchParams.set("creatorInterest", selectedValues.join(","));
//       } else {
//         searchParams.delete("creatorInterest");
//       }
//       return searchParams;
//     });
//   };

//   const handleAudienceInterestChange = (selectedList) => {
//     const updatedValues = selectedList.map((item) => `${item.name}-k-${selectedOptions[item.name] || "5"}`);
//     dispatch(setAudienceInterestFilter(updatedValues));

//     setSearchParam((searchParams) => {
//       if (updatedValues.length > 0) {
//         searchParams.set("audienceInterest", updatedValues.join(","));
//       } else {
//         searchParams.delete("audienceInterest");
//       }
//       return searchParams;
//     });

//     setIsDropdownOpen(false);
//   };

//   const handlePercentageChange = (name, value) => {
//     setSelectedOptions((prev) => {
//       const updatedOptions = {
//         ...prev,
//         [name]: value,
//       };

//       const updatedValues = audienceInterestArray.map((item) => {
//         const [interestName] = item.split('-k-');
//         return `${interestName}-k-${updatedOptions[interestName] || "5"}`;
//       });

//       dispatch(setAudienceInterestFilter(updatedValues));
//       setSearchParam((searchParams) => {
//         if (updatedValues.length > 0) {
//           searchParams.set("audienceInterest", updatedValues.join(","));
//         } else {
//           searchParams.delete("audienceInterest");
//         }
//         return searchParams;
//       });

//       return updatedOptions;
//     });

//     setIsDropdownOpen(false);
//   };

//   return (
//     <div ref={buttonRef} className="CreatorInterest flex justify-start">
//       <div className="relative w-full">
//         <button
//           onClick={handleSearchCreatorInterest}
//           className={`flex items-center bg-white ${isDropdownOpen ? "font-semibold" : ""}`}
//         >
//           <span className="filterTextColor flex items-center">
//             Interests
//             {isDropdownOpen ? (
//               <RxCross2 className="ml-2 text-lg" />
//             ) : (
//               <IoChevronDownSharp className="ml-2 text-lg" />
//             )}
//           </span>
//         </button>
//         <ul ref={dropdownRef} className={`absolute w-fit p-5 z-50 bg-white rounded-lg shadow-lg ${isDropdownOpen ? "block" : "hidden"}`}>
//           <div className="flex gap-5">
//             <div className="flex flex-col gap-2 w-full">
//               <div className="flex items-center gap-2">
//                 <MdPeopleAlt className="text-2xl" /> Audience
//                 <InfoIconTooltip
//                   infoContent={
//                     "Identify influencers by their interests. We determine interest of influencers by analyzing posts for caption text, mentions, hashtags and location tags. You can add several interests and your search results will be refined with influencers that have ANY of the specified interests."
//                   }
//                 />
//               </div>
//               <MultiSelectDropdown
//                 options={audienceInterest}
//                 onSelect={handleAudienceInterestChange}
//                 onRemove={handleAudienceInterestChange}
//                 placeholder={"Select Interest"}
//                 selectedValues={audienceInterestArray.map((value) => ({
//                   name: value.split('-k-')[0], // Extract interest name only
//                 }))}
//                 loading={loading}
//               />
//               {audienceInterestArray.map((item, index) => {
//                 const [interestName, percentage] = item.split('-k-');
//                 return (
//                   <div key={index} className="flex items-center justify-between">
//                     <span>{interestName}</span>
//                     <select
//                       value={selectedOptions[interestName] || percentage || "5"}
//                       onChange={(e) => handlePercentageChange(interestName, e.target.value)}
//                       className="cursor-pointer hover:bg-gray-200 rounded-lg shadow-md"
//                       style={{ border: "0.6px solid #D9D9D9", maxHeight: "400px", overflowY: "auto" }}
//                     >
//                       {percentageArr.map((option) => (
//                         <option key={option} value={option}>
//                           {`>${option}%`}
//                         </option>
//                       ))}
//                     </select>
//                   </div>
//                 );
//               })}
//             </div>

//             <div className="flex flex-col gap-2 w-full">
//               <div className="flex items-center gap-2">
//                 <MdCampaign className="text-2xl" /> Influencer
//                 <InfoIconTooltip
//                   infoContent={
//                     "Identify influencers by their interests. We determine interest of influencers by analyzing posts for caption text, mentions, hashtags and location tags. You can add several interests and your search results will be refined with influencers that have ANY of the specified interests."
//                   }
//                 />
//               </div>
//               <MultiSelectDropdown
//                 options={creatorInterest}
//                 onSelect={handleInterestChange}
//                 onRemove={handleInterestChange}
//                 placeholder={"Select Interest"}
//                 selectedValues={creatorInterestArray.map((value) => ({
//                   name: value,
//                 }))}
//                 loading={loading}
//               />
//             </div>
//           </div>
//         </ul>
//       </div>
//     </div>
//   );
// };

// export default CreatorInterest;

// import React, { useRef, useState, useEffect } from "react";
// import { RxCross2 } from "react-icons/rx";
// import { IoChevronDownSharp } from "react-icons/io5";
// import { MdCampaign, MdPeopleAlt } from "react-icons/md";
// import { useDispatch } from "react-redux";
// import { setAudienceInterestFilter, setCreatorInterestFilter } from "../../redux/filters/actions";
// import Multiselect from "multiselect-react-dropdown";
// import InfoIconTooltip from "./InfoIconTooltip";
// import axiosInstance from "../../interseptor";
// import { useLocation, useSearchParams } from "react-router-dom";
// import useDropdownPosition from "./useDropdownPosition";

// const percentageArr = ["1", "5", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55", "60", "65", "70", "75", "80", "85", "90", "95"];

// const CreatorInterest = ({ selectedPlatform }) => {
//   const dispatch = useDispatch();
//   const [searchParam, setSearchParam] = useSearchParams();
//   const location = useLocation();
//   const searchParams = new URLSearchParams(location.search);

//   const creatorInterestUrl = searchParams.get("creatorInterest");
//   const audienceInterestUrl = searchParams.get("audienceInterest");

//   // Standardized parsing function for interests
//   const parseInterestString = (interestStr, hasPercentage = false) => {
//     if (!interestStr) return [];
    
//     return interestStr.split(",").map((value) => {
//       const parts = value.split("-k-");
//       return {
//         id: parts[0],
//         name: parts[1] || parts[0], // If no name is provided, use the ID
//         percentage: hasPercentage && parts[2] ? parts[2] : "5"
//       };
//     });
//   };

//   // Parse interest arrays using standardized function
//   const creatorInterestArray = parseInterestString(creatorInterestUrl);
//   const audienceInterestArray = parseInterestString(audienceInterestUrl, true);

//   const {dropdownRef, buttonRef, setIsDropdownOpen, isDropdownOpen} = useDropdownPosition();

//   const [creatorInterest, setCreatorInterest] = useState([]);
//   const [audienceInterest, setAudienceInterest] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [searchString, setSearchString] = useState("");
//   const [selectedOptions, setSelectedOptions] = useState({});

//   // Initialize selectedOptions from audienceInterestArray
//   useEffect(() => {
//     const initialOptions = {};
//     audienceInterestArray.forEach(item => {
//       initialOptions[item.id] = item.percentage;
//     });
//     setSelectedOptions(initialOptions);
//   }, [audienceInterestUrl]);

//   const fetchCreatorInterestData = async () => {
//     setCreatorInterest([]);
//     setAudienceInterest([]);
//     try {
//       setLoading(true);
//       setError(null);

//       const formData = new FormData();
//       formData.append("filter", "interests");
//       formData.append("logintype", "getfilters");
//       formData.append("device_id", "search");
//       formData.append("platform", selectedPlatform);
//       formData.append("search_string", searchString);

//       const response = await axiosInstance.post("livesearch/getfilters", formData);
//       const result = response.data;

//       if (result.status === 1) {
//         setCreatorInterest(result.response.data.interests);
//         setAudienceInterest(result.response.data.interests);
//       } else {
//         setError(result.msg);
//         console.error("Error:", result.msg);
//       }
//     } catch (error) {
//       setError("An error occurred while fetching data.");
//       console.error("Error:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleSearchCreatorInterest = () => {
//     setSearchString("");
//     setIsDropdownOpen(!isDropdownOpen);
//     fetchCreatorInterestData();
//   };

//   // Updated to match CreatorLocation pattern
//   const handleInterestChange = (selectedList) => {
//     const selectedValues = selectedList?.map(
//       (item) => `${item.id}-k-${item.name}`
//     );
    
//     dispatch(setCreatorInterestFilter(selectedValues));

//     setSearchParam((searchParams) => {
//       if (selectedValues.length > 0) {
//         searchParams.set("creatorInterest", selectedValues.join(","));
//       } else {
//         searchParams.delete("creatorInterest");
//       }
//       return searchParams;
//     });
//   };

//   // Updated to match CreatorLocation pattern
//   const handleAudienceInterestChange = (selectedList) => {
//     const selectedValues = selectedList.map((item) => 
//       `${item.id}-k-${item.name}-k-${selectedOptions[item.id] || "5"}`
//     );
    
//     dispatch(setAudienceInterestFilter(selectedValues));

//     setSearchParam((searchParams) => {
//       if (selectedValues.length > 0) {
//         searchParams.set("audienceInterest", selectedValues.join(","));
//       } else {
//         searchParams.delete("audienceInterest");
//       }
//       return searchParams;
//     });
//   };

//   const handlePercentageChange = (id, value) => {
//     setSelectedOptions((prev) => {
//       const updatedOptions = {
//         ...prev,
//         [id]: value,
//       };

//       const updatedValues = audienceInterestArray.map((item) => 
//         `${item.id}-k-${item.name}-k-${updatedOptions[item.id] || "5"}`
//       );

//       dispatch(setAudienceInterestFilter(updatedValues));
//       setSearchParam((searchParams) => {
//         if (updatedValues.length > 0) {
//           searchParams.set("audienceInterest", updatedValues.join(","));
//         } else {
//           searchParams.delete("audienceInterest");
//         }
//         return searchParams;
//       });

//       return updatedOptions;
//     });
//   };

//   // Map selected values for MultiSelectDropdown
//   const selectedValueAudience = audienceInterestArray.map((item) => ({
//     id: item.id,
//     name: item.name
//   }));

//   const selectedValueCreator = creatorInterestArray.map((item) => ({
//     id: item.id,
//     name: item.name
//   }));

//   // Effect to fetch data when search string changes
//   useEffect(() => {
//     if (searchString === "") {
//       fetchCreatorInterestData();
//     }
//   }, [searchString]);

//   useEffect(() => {
//     if (searchString.length > 1 && selectedPlatform === "Instagram") {
//       fetchCreatorInterestData();
//     }
//   }, [searchString]);

//   // Common styles for Multiselect component
//   const multiselectStyles = {
//     multiselectContainer: {
//       width: "100%",
//       borderRadius: "0.375rem",
//       boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
//     },
//     searchBox: {
//       border: "0.6px solid #D9D9D9",
//       borderRadius: "0.375rem",
//     },
//     inputField: {
//       fontSize: "14px",
//       fontWeight: "400",
//       color: "#333",
//     },
//     chips: {
//       background: "#6d5eac",
//       color: "#fff",
//     },
//     option: {
//       color: "#333",
//     },
//     groupHeading: {
//       color: "#6d5eac",
//       background: "#6d5eac",
//     },
//     dropdownList: {
//       background: "#6d5eac",
//       "&:hover": {
//         background: "#6d5eac",
//       },
//     },
//   };

//   return (
//     <div ref={buttonRef} className="CreatorInterest flex justify-start">
//       <div className="relative w-full">
//         <button
//           onClick={handleSearchCreatorInterest}
//           className={`flex items-center bg-white ${isDropdownOpen ? "font-semibold" : ""}`}
//         >
//           <span className="filterTextColor flex items-center">
//             Interests
//             {isDropdownOpen ? (
//               <RxCross2 className="ml-2 text-lg" />
//             ) : (
//               <IoChevronDownSharp className="ml-2 text-lg" />
//             )}
//           </span>
//         </button>
//         <ul ref={dropdownRef} className={`absolute w-fit p-5 z-50 bg-white rounded-lg shadow-lg ${isDropdownOpen ? "block" : "hidden"}`}>
//           <div className="flex gap-5">
//             <div className="flex flex-col gap-2 w-full">
//               <div className="flex items-center gap-2">
//                 <MdPeopleAlt className="text-2xl" /> Audience
//                 <InfoIconTooltip
//                   infoContent={
//                     "Identify influencers by their interests. We determine interest of influencers by analyzing posts for caption text, mentions, hashtags and location tags. You can add several interests and your search results will be refined with influencers that have ANY of the specified interests."
//                   }
//                 />
//               </div>
//               <Multiselect
//                 className="bg-white"
//                 options={audienceInterest}
//                 displayValue="name"
//                 selectedValues={selectedValueAudience}
//                 onSelect={handleAudienceInterestChange}
//                 onRemove={handleAudienceInterestChange}
//                 placeholder="Search Interest"
//                 loading={loading}
//                 onSearch={(searchInput) => setSearchString(searchInput)}
//                 style={multiselectStyles}
//               />
//               {audienceInterestArray.map((item) => (
//                 <div key={item.id} className="flex items-center justify-between">
//                   <span>{item.name}</span>
//                   <select
//                     value={selectedOptions[item.id] || item.percentage || "5"}
//                     onChange={(e) => handlePercentageChange(item.id, e.target.value)}
//                     className="cursor-pointer hover:bg-gray-200 rounded-lg shadow-md"
//                     style={{ border: "0.6px solid #D9D9D9", maxHeight: "400px", overflowY: "auto" }}
//                   >
//                     {percentageArr.map((option) => (
//                       <option key={option} value={option}>
//                         {`>${option}%`}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//               ))}
//             </div>

//             <div className="flex flex-col gap-2 w-full">
//               <div className="flex items-center gap-2">
//                 <MdCampaign className="text-2xl" /> Influencer
//                 <InfoIconTooltip
//                   infoContent={
//                     "Identify influencers by their interests. We determine interest of influencers by analyzing posts for caption text, mentions, hashtags and location tags. You can add several interests and your search results will be refined with influencers that have ANY of the specified interests."
//                   }
//                 />
//               </div>
//               <Multiselect
//                 className="bg-white"
//                 options={creatorInterest}
//                 displayValue="name"
//                 onSelect={handleInterestChange}
//                 onRemove={handleInterestChange}
//                 selectedValues={selectedValueCreator}
//                 placeholder="Search Interest"
//                 loading={loading}
//                 onSearch={(searchInput) => setSearchString(searchInput)}
//                 style={multiselectStyles}
//               />
//             </div>
//           </div>
//         </ul>
//       </div>
//     </div>
//   );
// };

// export default CreatorInterest;
